import Vue, { PluginFunction } from 'vue'
import { Route } from 'vue-router'

export interface Breadcrumb {
  name: string
  path?: string
}

export interface Breadcrumbs {
  setParams(params?: { [key: string]: number | string }): void
  get: () => Breadcrumb[]
  set: (breadcrumbs: Breadcrumb[]) => void
}

const interpolate = (
  template: string,
  params: { [key: string]: string }
): string =>
  Object.keys(params).reduce(
    (acc, key) => acc.replace(new RegExp(`:${key}`, 'g'), params[key]),
    template
  )

const state = Vue.observable<{
  breadcrumbs: Breadcrumb[]
}>({
  breadcrumbs: [],
})

export const getters = {
  breadcrumbs: () => state.breadcrumbs,
}

const mutations = {
  setBreadcrumbs: (breadcrumbs: Breadcrumb[]) => {
    state.breadcrumbs = breadcrumbs
  },
}

const getMetaBreadcrumbs = (route: Route): Breadcrumb[] =>
  (route.meta && route.meta.breadcrumbs) ?? []

const getMetaBreadcrumbParams = (route: Route): string[] =>
  (route.meta && route.meta.breadcrumbParams) ?? []

const getEmptyBreadcrumbParams = (route: Route): { [key: string]: '' } => {
  const metaBreadcrumbParams = getMetaBreadcrumbParams(route)
  return metaBreadcrumbParams.reduce((acc, key) => ({ ...acc, [key]: '' }), {})
}

// パンくず用のVueプラグイン
const install: PluginFunction<{}> = (Vue) => {
  Vue.mixin({
    beforeRouteEnter(to, _, next) {
      const metaBreadcrumbs = getMetaBreadcrumbs(to)
      const emptyParams = getEmptyBreadcrumbParams(to)

      mutations.setBreadcrumbs(
        metaBreadcrumbs.map(({ name, path }) => ({
          name: interpolate(name, emptyParams),
          path: path ? interpolate(path, emptyParams) : '',
        }))
      )

      next()
    },
  })

  // $breadcrumbsプロパティの定義
  Object.defineProperty(Vue.prototype, '$breadcrumbs', {
    get(): Breadcrumbs {
      const setParams = (params: { [key: string]: string }) => {
        const metaBreadcrumbs = getMetaBreadcrumbs(this.$route)
        const metaBreadcrumbParams = getMetaBreadcrumbParams(this.$route)

        mutations.setBreadcrumbs(
          metaBreadcrumbs.map(({ name, path }) => ({
            name:
              params && metaBreadcrumbParams.length
                ? interpolate(name, params)
                : name,
            path:
              params && metaBreadcrumbParams.length && path
                ? interpolate(path, params)
                : '',
          }))
        )
      }

      const set = (breadcrumbs: Breadcrumb[]) => {
        mutations.setBreadcrumbs(breadcrumbs)
      }

      return {
        setParams,
        get: getters.breadcrumbs,
        set,
      }
    },
  })
}

export default {
  install,
}
